import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Compass, CheckCircle2 } from "lucide-react";
import { useConsultation } from "@/context/ConsultationContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { INTEREST_OPTIONS, TIMELINE_OPTIONS, BUDGET_OPTIONS, JOURNEY_OPTIONS } from "@/data/site";

const empty = { name: "", email: "", phone: "", country: "India", interest: "", timeline: "", budget: "", journey_type: "", message: "" };

export function ConsultationDialog() {
  const { open, setOpen, preset } = useConsultation();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...empty, interest: preset || "" });
      setDone(false);
    }
  }, [open, preset]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Your journey has begun. We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full rounded-xl border border-navy/15 bg-warm px-4 py-3 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="consultation-dialog" className="max-w-lg bg-warm border-none p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="bg-navy px-7 py-6 text-warm">
          <div className="flex items-center gap-2 text-gold accent-eyebrow"><Compass className="h-4 w-4" /> Start Your Portugal Journey</div>
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl mt-2 text-warm font-normal">Book Your Free Consultation</DialogTitle>
          </DialogHeader>
          <p className="text-warm/60 text-sm mt-1">Tell us where you are. We'll show you the way forward.</p>
        </div>

        {done ? (
          <div data-testid="consultation-success" className="p-10 text-center">
            <CheckCircle2 className="h-14 w-14 text-olive mx-auto" strokeWidth={1.5} />
            <h3 className="font-playfair text-2xl text-navy mt-5">Thank you.</h3>
            <p className="text-navy/60 mt-2 max-w-sm mx-auto">Your journey has begun. A Portugal specialist from Effizent Europe will reach out within one business day.</p>
            <button onClick={() => setOpen(false)} data-testid="consultation-close" className="mt-7 rounded-full bg-navy px-8 py-3 text-sm font-600 text-warm hover:bg-navy-800 transition-colors">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="lead-name" required placeholder="Full name" value={form.name} onChange={set("name")} className={field} />
              <input data-testid="lead-country" placeholder="Country" value={form.country} onChange={set("country")} className={field} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="lead-email" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={field} />
              <input data-testid="lead-phone" required placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className={field} />
            </div>
            <select data-testid="lead-interest" value={form.interest} onChange={set("interest")} className={field}>
              <option value="">What are you interested in?</option>
              {INTEREST_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="lead-journey" value={form.journey_type} onChange={set("journey_type")} className={field}>
                <option value="">Journey type</option>
                {JOURNEY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <select data-testid="lead-timeline" value={form.timeline} onChange={set("timeline")} className={field}>
                <option value="">Timeline</option>
                {TIMELINE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <select data-testid="lead-budget" value={form.budget} onChange={set("budget")} className={field}>
              <option value="">Budget range (optional)</option>
              {BUDGET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <textarea data-testid="lead-message" placeholder="Tell us about your dream for Portugal (optional)" value={form.message} onChange={set("message")} rows={3} className={field} />
            <button data-testid="lead-submit" disabled={loading} className="w-full rounded-full bg-gold py-3.5 font-600 text-navy hover:bg-gold-soft transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-60">
              {loading ? "Sending…" : "Begin My Journey"}
            </button>
            <p className="text-center text-xs text-navy/40">Free, confidential, no obligation. GDPR compliant.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
